import { useCallback, useRef } from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { localMuteAtom } from "../../recoil/atoms/local-mute"
import { playerPropsAtom } from "../../recoil/atoms/player"
import { watchPartyCallbackSelector } from "../../recoil/atoms/watchPartyScreen"
import { ConsoleLogger } from "../../util/logger"
import { waitForElementForPeriod } from "../../util/waitForElm"

const logger = new ConsoleLogger()

function fadeOutVolume(videoElement: HTMLVideoElement) {
  // Fade out over 2 secs.
  return new Promise((resolve, reject) => {
    let interval = 4
    const reduceVolumeBy = videoElement.volume / interval
    const secondInterval = 2000 / interval

    const timerId = setInterval(async () => {
      videoElement.volume = +(videoElement.volume - reduceVolumeBy).toFixed(2)
      interval--
      if (interval === 0) {
        clearInterval(timerId)
        videoElement.muted = true
        await videoElement.play()
        resolve(true)
      }
    }, secondInterval)
  })
}

function fadeInVolume(videoElement: HTMLVideoElement, requiredVolume: number) {
  // Fade out over 2 secs.
  return new Promise((resolve, reject) => {
    let interval = 4
    videoElement.muted = false
    videoElement.play()
    const increaseVolumeBy = requiredVolume / interval
    const secondInterval = 2000 / interval

    const timerId = setInterval(async () => {
      videoElement.volume = +(videoElement.volume + increaseVolumeBy).toFixed(2)
      interval--
      if (interval === 0) {
        clearInterval(timerId)
        videoElement.muted = false
        await videoElement.play()
        resolve(true)
      }
    }, secondInterval)
  })
}

const useMNPAudio = () => {
  const setLocalMuted = useSetRecoilState(localMuteAtom)
  const watchPartyCallback = useRecoilValue(watchPartyCallbackSelector)
  const setPlayer = useSetRecoilState(playerPropsAtom)

  const volumeRef = useRef(0)

  const fadeOutMNPAudio = useCallback(async () => {
    const watchPartyHostId = await watchPartyCallback.getWatchPartyHostId()

    try {
      // check if MNP video
      // If MNP video is present - mute it
      if (watchPartyHostId === "modNotPresentVideo") {
        const videoEle = (await waitForElementForPeriod(
          "watchpartyvideoplayer",
          1000
        )) as HTMLVideoElement
        volumeRef.current = videoEle.volume
        await fadeOutVolume(videoEle)
      }
      setLocalMuted(true)
      

      setPlayer({paused: true})
    } catch (error) {
      logger.log("Error: while muting MNP video Element")
      setLocalMuted(true)
      return false
    }
    return true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fadeInMNPAudio = useCallback(async () => {
    const watchPartyHostId = await watchPartyCallback.getWatchPartyHostId()
    try {
      setLocalMuted(false)
      if (watchPartyHostId === "modNotPresentVideo") {
        // If MNP video is present - mute it
        const videoEle = (await waitForElementForPeriod(
          "watchpartyvideoplayer",
          4000
        )) as HTMLVideoElement
        videoEle.volume = 0
        videoEle.muted = false
        fadeInVolume(videoEle, 0.75)
      }
    } catch (error) {
      logger.log("Error: while un-muting MNP video Element")
      return false
    }
    return true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    fadeOutMNPAudio,
    fadeInMNPAudio
  }
}

export default useMNPAudio
